/**
 * The id used to address an event on the REST API (HNS-148).
 *
 * Always a string: either the UUID `publicId` or, during the window where this app is deployed
 * ahead of the backend promote, the stringified numeric id. The backend accepts both
 * (`EventIdentifier`), and a stringified numeric still registers as `numeric` in the
 * `event.id.shape` burn-down, so a straggler stays visible rather than turning into a
 * `Number(uuid) === NaN` request.
 *
 * Typing this as `string` rather than `string | number` is deliberate: it forces every caller
 * through this helper, so the UUID-only flip is a change to this one function.
 *
 * NOT for the WebSocket layer, which stays keyed on the internal numeric id — take that from
 * `selectActiveEventId`.
 */
export type EventApiId = string;

type EventIdSource = { id: number; publicId?: string };

export function toEventApiId(event: EventIdSource): EventApiId;
export function toEventApiId(event: EventIdSource | undefined): EventApiId | undefined;
export function toEventApiId(event: EventIdSource | undefined): EventApiId | undefined {
  return event == null ? undefined : (event.publicId ?? String(event.id));
}

/** As {@link toEventApiId}, for callers holding the two ids separately rather than an event object. */
export function eventApiId(publicId: string | undefined, numericId: number): EventApiId;
export function eventApiId(publicId: string | undefined, numericId: number | undefined): EventApiId | undefined;
export function eventApiId(publicId: string | undefined, numericId: number | undefined): EventApiId | undefined {
  return publicId ?? (numericId == null ? undefined : String(numericId));
}
